import axios from 'axios';

// API base configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth-storage');
    if (token) {
      try {
        const authData = JSON.parse(token);
        if (authData.state?.token) {
          config.headers.Authorization = `Bearer ${authData.state.token}`;
        }
      } catch (error) {
        console.error('Error parsing auth token:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear local authentication information
      localStorage.removeItem('auth-storage');
      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (username: string, password: string) =>
    api.post('/api/auth/login', { username, password }),
  
  logout: () => api.post('/api/auth/logout'),
  
  getCurrentUser: () => api.get('/api/auth/me'),
  
  refreshToken: () => api.post('/api/auth/refresh'),
};

// Dashboard API
export const dashboardAPI = {
  getOverview: (params?: any) => api.get('/api/dashboard/overview', { params }),
  
  getKPI: (params?: any) => api.get('/api/dashboard/kpi', { params }),
  
  getProductsPerformance: (params?: any) => api.get('/api/dashboard/products-performance', { params }),
  
  getSalesDataTable: (params?: any) => api.get('/api/dashboard/sales-data-table', { params }),
  
  getChannelRanking: (params?: any) => api.get('/api/dashboard/channel-ranking', { params }),
  
  getSkuRanking: (params?: any) => api.get('/api/dashboard/sku-ranking', { params }),
};

// Product API
export const productsAPI = {
  getProducts: (params?: any) => api.get('/api/products', { params }),
  
  getProduct: (id: number) => api.get(`/api/products/${id}`),
  
  createProduct: (data: any) => api.post('/api/products', data),
  
  updateProduct: (id: number, data: any) => api.put(`/api/products/${id}`, data),
  
  deleteProduct: (id: number) => api.delete(`/api/products/${id}`),
  
  getCategories: () => api.get('/api/products/categories'),
  
  searchProducts: (query: string) => api.get('/api/products/search', { params: { q: query } }),
};

// Order API
export const ordersAPI = {
  getOrders: (params?: any) => api.get('/api/orders', { params }),
  
  getOrder: (id: number) => api.get(`/api/orders/${id}`),
  
  createOrder: (data: any) => api.post('/api/orders', data),
  
  updateOrderStatus: (id: number, status: string) => 
    api.put(`/api/orders/${id}/status`, { status }),
  
  getOrderStats: (params?: any) => api.get('/api/orders/stats/summary', { params }),
  
  exportOrdersCSV: (params?: any) => api.get('/api/orders/export/csv', { params }),
};

// Analytics API
export const analyticsAPI = {
  getSalesAnalytics: (params?: any) => api.get('/api/analytics/sales', { params }),
  
  getChannelPerformance: (params?: any) => api.get('/api/analytics/channels', { params }),
  
  getProductPerformance: (params?: any) => api.get('/api/analytics/products', { params }),
  
  getSalesTrends: (params?: any) => api.get('/api/analytics/trends/sales', { params }),
  
  getCohortAnalysis: () => api.get('/api/analytics/cohort'),
  
  getSalesForecast: (params?: any) => api.get('/api/analytics/forecast', { params }),
};

// User API
export const usersAPI = {
  getUsers: (params?: any) => api.get('/api/users', { params }),
  
  getUser: (id: number) => api.get(`/api/users/${id}`),
  
  createUser: (data: any) => api.post('/api/users', data),
  
  updateUser: (id: number, data: any) => api.put(`/api/users/${id}`, data),
  
  deleteUser: (id: number) => api.delete(`/api/users/${id}`),
};

// Common API utility functions
export const apiUtils = {
  // Handle API errors
  handleError: (error: any) => {
    if (error.response) {
      // Server response error
      const message = error.response.data?.detail || error.response.data?.message || 'Server error';
      return { success: false, message, status: error.response.status };
    } else if (error.request) {
      // Network error
      return { success: false, message: 'Network connection error', status: 0 };
    } else {
      // Other errors
      return { success: false, message: error.message || 'Unknown error', status: -1 };
    }
  },
  
  // Format API response
  formatResponse: (response: any) => {
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  },
};

export default api;